/**
 * Image size presets and the per-format generation resolver.
 *
 * A preset names a print FOOTPRINT (how much of the page the image will
 * cover), not a pixel size: the pixels needed to print that footprint
 * sharply depend on the book format's physical dimensions, so the
 * resolver — not the preset — picks the Gemini model, output size, and
 * aspect ratio. "Half page" on an 8×8 in book spans 8 physical inches
 * and needs a 4K render; the same preset on A5 spans 5.8 in and 2K is
 * plenty. Keeping the tier per-format means small formats generate
 * cheaper automatically.
 *
 * The price table lives here (not in api/) so the UI's upfront cost
 * chips, the API's usage logging, and the resolver's estimates can never
 * disagree. The operator-editable `model_pricing` D1 table still
 * overrides these at billing time.
 */
import { MM_PER_INCH, formatPageInches } from './constants';
import type { FormatPreset, SpreadImageCrop, SpreadKind } from './types';

export type ImageSizePreset =
  | 'spread'
  | 'full-page'
  | 'large'
  | 'half-wide'
  | 'half-tall'
  | 'spot'
  | 'mini';

export type GeminiImageSize = '1K' | '2K' | '4K';

export type GeminiAspectRatio = '1:1' | '16:9' | '9:16' | '4:3' | '3:4';

/**
 * A preset's footprint in PAGE fractions: `pageW` is the covered fraction
 * of a single page's width (2 = both pages of a spread), `pageH` of its
 * height. `edgeToEdge` footprints print to the cut line, so their pixel
 * need includes bleed and the print shop trims into the art (never white).
 *
 * Floating footprints (edgeToEdge: false — spot art) apply BOTH fractions
 * to the page's SHORT side instead: a spot is square on every format, and
 * the default 'spot' preset stays exactly the pre-preset generation tier
 * (standard model, 1:1, ~1K) on tall and wide books too.
 */
export interface ImageSizePresetDef {
  value: ImageSizePreset;
  label: string;
  hint: string;
  pageW: number;
  pageH: number;
  edgeToEdge: boolean;
}

export const IMAGE_SIZE_PRESETS: readonly ImageSizePresetDef[] = [
  {
    value: 'spot',
    label: 'Spot',
    hint: 'Floating spot illustration — the classic picture-book vignette',
    pageW: 0.42,
    pageH: 0.42,
    edgeToEdge: false,
  },
  {
    value: 'mini',
    label: 'Mini',
    hint: 'Small margin art — doodles, corner accents',
    pageW: 0.25,
    pageH: 0.25,
    edgeToEdge: false,
  },
  {
    value: 'half-wide',
    label: 'Half · wide',
    hint: 'Banner across the full page width, half its height',
    pageW: 1,
    pageH: 0.5,
    edgeToEdge: true,
  },
  {
    value: 'half-tall',
    label: 'Half · tall',
    hint: 'Column beside the text — half the width, full height',
    pageW: 0.5,
    pageH: 1,
    edgeToEdge: true,
  },
  {
    value: 'large',
    label: 'Large',
    hint: 'Most of the page, with a band left for text',
    pageW: 1,
    pageH: 0.7,
    edgeToEdge: true,
  },
  {
    value: 'full-page',
    label: 'Full page',
    hint: 'Edge-to-edge single page',
    pageW: 1,
    pageH: 1,
    edgeToEdge: true,
  },
  {
    value: 'spread',
    label: 'Full spread',
    hint: 'Edge-to-edge across both facing pages',
    pageW: 2,
    pageH: 1,
    edgeToEdge: true,
  },
] as const;

/** Bank takes default small: no placement is known yet, and the editor's
 *  sharpness badge catches any later promotion to a bigger footprint. */
export const DEFAULT_BANK_SIZE_PRESET: ImageSizePreset = 'spot';

export function normalizeSizePreset(raw: unknown): ImageSizePreset | null {
  return IMAGE_SIZE_PRESETS.some((p) => p.value === raw)
    ? (raw as ImageSizePreset)
    : null;
}

/**
 * The generation spec a stored (possibly untrusted/legacy) preset value
 * implies: null for uploads and pre-preset takes, which generate at the
 * legacy standard/square tier. The single rule behind "refine, variants,
 * and more-like-this run at the source take's tier".
 */
export function resolveInheritedSpec(
  raw: unknown,
  formatPreset: FormatPreset | null | undefined,
): GenerationSpec | null {
  const preset = normalizeSizePreset(raw);
  return preset ? resolveGenerationSpec(preset, formatPreset) : null;
}

// ── Print-sharpness thresholds ──────────────────────────────────────────
// CCS-style print shops ask for 300 dpi at final size (≤ A3). The sharp
// gate sits slightly below so float drift and ratio snapping never flag a
// placement that was sized exactly to a preset.

/** The dpi the resolver sizes generations for. */
export const TARGET_PRINT_DPI = 300;
/** At or above: prints sharp. */
export const SHARP_MIN_DPI = 280;
/** At or above (but under SHARP_MIN_DPI): slightly soft. Below: blurry. */
export const SOFT_MIN_DPI = 200;

/** Bleed per edge the print shop trims off (mm) — edge-to-edge footprints
 *  must carry pixels for it. */
export const PRINT_BLEED_MM = 3;

const CM_PER_INCH = 2.54;

// ── Pricing ─────────────────────────────────────────────────────────────
// Flat USD per generated image, keyed by the pricing key logImageUsage
// receives (`GeneratedImage.pricingKey`). The pro model bills 1K and 2K
// identically; 4K outputs cost ~1.8× (Google bills ~2000 vs ~1120 output
// tokens per image), applied to the same base as the operator-set 2K
// price. The `model_pricing` D1 table overrides any of these at runtime.

export const PRO_4K_PRICING_KEY = 'gemini-3-pro-image-preview@4k';

/** 4K outputs bill ~2000 output tokens vs ~1120 for 1K/2K — this ratio
 *  derives the 4K price from the pro base price, including when an
 *  operator overrides the base via the `model_pricing` D1 table. */
export const PRO_4K_PRICE_RATIO = 2000 / 1120;

export const IMAGE_MODEL_PRICES_USD: Record<string, number> = {
  'gemini-2.5-flash-image': 0.039,
  'gemini-3-pro-image-preview': 0.075,
  [PRO_4K_PRICING_KEY]: Math.round(0.075 * PRO_4K_PRICE_RATIO * 1000) / 1000,
};

// ── Resolver ────────────────────────────────────────────────────────────

export interface GenerationSpec {
  preset: ImageSizePreset;
  quality: 'standard' | 'pro';
  /** Long-side output size requested from Gemini. '1K' rides the standard
   *  model's native size; 2K/4K require the pro model. */
  imageSize: GeminiImageSize;
  aspectRatio: GeminiAspectRatio;
  /** Long-side pixels the chosen tier delivers. */
  longSidePx: number;
  /** Estimated cost per image (USD) — display + preflight only; billing
   *  reads the `model_pricing` table via the pricing key. */
  estCostUsd: number;
  /** Physical print footprint (no bleed), for "≈ 20 × 10 cm" captions. */
  printWidthCm: number;
  printHeightCm: number;
}

const RATIO_CANDIDATES: Array<[GeminiAspectRatio, number]> = [
  ['1:1', 1],
  ['4:3', 4 / 3],
  ['3:4', 3 / 4],
  ['16:9', 16 / 9],
  ['9:16', 9 / 16],
];

/** Snap a physical width/height ratio to the nearest Gemini ratio
 *  (log-distance, so 2.0 → 16:9 and 0.5 → 9:16 symmetrically). */
export function snapAspectRatio(ratio: number): GeminiAspectRatio {
  const r = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  let best: GeminiAspectRatio = '1:1';
  let bestDist = Infinity;
  for (const [name, value] of RATIO_CANDIDATES) {
    const dist = Math.abs(Math.log(r / value));
    if (dist < bestDist) {
      bestDist = dist;
      best = name;
    }
  }
  return best;
}

/**
 * Resolve a footprint preset against a book format: the cheapest
 * model/size tier that prints the footprint at TARGET_PRINT_DPI
 * (SHARP_MIN_DPI tolerance), plus the snapped aspect ratio and the cost
 * estimate. Footprints too large even for 4K (a wide spread) still
 * resolve to 4K — physical sizes past A3 relax the dpi requirement.
 */
export function resolveGenerationSpec(
  preset: ImageSizePreset,
  formatPreset: FormatPreset | null | undefined,
): GenerationSpec {
  const def =
    IMAGE_SIZE_PRESETS.find((p) => p.value === preset) ??
    IMAGE_SIZE_PRESETS.find((p) => p.value === DEFAULT_BANK_SIZE_PRESET)!;
  const { w: pageW, h: pageH } = formatPageInches(formatPreset);

  // Floating spot art measures against the page's short side (square on
  // every format); edge-to-edge footprints measure the page axes they
  // actually span.
  const shortSide = Math.min(pageW, pageH);
  const printW = def.pageW * (def.edgeToEdge ? pageW : shortSide);
  const printH = def.pageH * (def.edgeToEdge ? pageH : shortSide);
  const bleedIn = def.edgeToEdge ? (2 * PRINT_BLEED_MM) / MM_PER_INCH : 0;
  const neededLongPx =
    (Math.max(printW, printH) + bleedIn) * TARGET_PRINT_DPI;

  // Accept a tier when its long side reaches SHARP_MIN_DPI for the
  // footprint — the same gate the editor badge uses.
  const acceptablePx = (neededLongPx / TARGET_PRINT_DPI) * SHARP_MIN_DPI;

  let quality: 'standard' | 'pro';
  let imageSize: GeminiImageSize;
  let longSidePx: number;
  let pricingKey: string;
  if (acceptablePx <= 1024) {
    quality = 'standard';
    imageSize = '1K';
    longSidePx = 1024;
    pricingKey = 'gemini-2.5-flash-image';
  } else if (acceptablePx <= 2048) {
    quality = 'pro';
    imageSize = '2K';
    longSidePx = 2048;
    pricingKey = 'gemini-3-pro-image-preview';
  } else {
    quality = 'pro';
    imageSize = '4K';
    longSidePx = 4096;
    pricingKey = PRO_4K_PRICING_KEY;
  }

  return {
    preset: def.value,
    quality,
    imageSize,
    aspectRatio: snapAspectRatio(printW / printH),
    longSidePx,
    estCostUsd: IMAGE_MODEL_PRICES_USD[pricingKey] ?? 0,
    printWidthCm: printW * CM_PER_INCH,
    printHeightCm: printH * CM_PER_INCH,
  };
}

// ── Effective print resolution of a PLACED image ────────────────────────

export type SharpnessLevel = 'sharp' | 'soft' | 'blurry';

export function classifySharpness(dpi: number): SharpnessLevel {
  if (dpi >= SHARP_MIN_DPI) return 'sharp';
  if (dpi >= SOFT_MIN_DPI) return 'soft';
  return 'blurry';
}

/**
 * Effective print dpi of an image placed on a spread canvas: visible
 * source pixels ÷ printed inches. Placement uses object-fit: cover of the
 * crop region (uniform scale), so dpi is the limiting axis of
 * crop-px / box-inches. Returns Infinity for degenerate boxes so empty
 * placements never warn.
 */
export function effectiveImageDpi(
  el: { w: number; h: number; crop?: SpreadImageCrop | null },
  imagePxW: number,
  imagePxH: number,
  formatPreset: FormatPreset | null | undefined,
  kind: SpreadKind,
): number {
  if (imagePxW <= 0 || imagePxH <= 0) return Infinity;
  const { w: pageW, h: pageH } = formatPageInches(formatPreset);
  const canvasWIn = kind === 'spread' ? pageW * 2 : pageW;
  const boxWIn = (el.w / 100) * canvasWIn;
  const boxHIn = (el.h / 100) * pageH;
  if (boxWIn <= 0 || boxHIn <= 0) return Infinity;

  let cropW = imagePxW;
  let cropH = imagePxH;
  if (el.crop && el.crop.w > 0 && el.crop.h > 0) {
    cropW = (el.crop.w / 100) * imagePxW;
    cropH = (el.crop.h / 100) * imagePxH;
  }
  // Cover-fit scales uniformly until both axes cover the box; the axis
  // that constrains the scale sets the print resolution.
  return Math.min(cropW / boxWIn, cropH / boxHIn);
}

/**
 * The factor (≤ 1) to scale a placed element's box down so it prints
 * sharp at its current image. 1 when it already does.
 */
export function shrinkToSharpFactor(dpi: number): number {
  if (!Number.isFinite(dpi) || dpi <= 0 || dpi >= SHARP_MIN_DPI) return 1;
  return dpi / SHARP_MIN_DPI;
}
